// ─── Friendly-URL Resolution Loader Helper (ADR 0012 D3/D5) ───
// Shared by the canonical routes (/app/l/$listRef, /app/s/$spaceRef) and the
// permanent back-compat alias routes (routes/app/spaces/$spaceId(.tsx) and
// .../lists/$listId.tsx). Wraps GET /resolve with the identical 404 mapping so
// every friendly-URL loader treats "out of scope or nonexistent" as a route
// `notFound()` — the resolve endpoint's whole point is that these two cases are
// indistinguishable by design (see the ADR's Security invariant: a bookmarked
// link opened by someone without access must 404, never leak a 403).

import { notFound, redirect } from '@tanstack/react-router';
import type { ResolvedEntity, ResolveType } from '@a8-planner/shared';
import { resolveApi } from './api/resolve';
import { ApiError } from './api-client';

/**
 * Calls GET /resolve and maps failures to the right route outcome:
 * - 404 (out-of-scope or nonexistent — identical by design) → `notFound()`.
 * - 401 (cold logged-out load — the resolve fires from a loader before auth
 *   fully bootstraps) → smooth in-SPA `redirect` to /login?returnTo=<friendly
 *   url>, matching the item-1 deep-link pattern (ADR 0012 D3 SSO sequence).
 *   resolveApi passes suppressAuthRedirect so the api-client's global hard
 *   `window.location` reload never fires first — this keeps a shared friendly
 *   link's logged-out open on the smooth return-to path, not a full reload.
 * - Anything else (network, 400 malformed, 409 ambiguous-short-id — the last
 *   astronomically unlikely at single-tenant scale, ADR D1) propagates to the
 *   router's error boundary; no dedicated 409 disambiguation UI this iteration.
 */
export async function resolveEntity(
  type: ResolveType,
  short: string,
  slug?: string,
): Promise<ResolvedEntity> {
  try {
    const response = await resolveApi.resolve({ type, short, slug });
    return response.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw notFound();
    }
    if (err instanceof ApiError && err.status === 401) {
      throw redirect({
        to: '/login',
        search: { returnTo: window.location.pathname + window.location.search },
        replace: true,
      });
    }
    throw err;
  }
}
